import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Flag } from '@neo/primitives/icon'
import { capitalize } from 'lodash'
import { NodevarList } from 'v2/pages/flow/components/nodevar-list'
import type { RLanguage, RProviders } from 'v2/pages/flow/sidebar/types'
import {
  audioTranscriptionConfigSchema,
  invalidSelectValueSchema,
} from '../../config/validation.config'
import { SUPPORTED_STT_PROVIDERS } from '../../constants'
import type { AiSkillsNodeData } from '../../types'

type FormValues = AiSkillsNodeData['config']

export const getAudioTranscriptionFields = (
  nodeVars: string[],
  providers?: RProviders,
  languages?: RLanguage[],
): GenericFieldConfig<FormValues> => [
  {
    name: 'audio_transcription_config.language',
    componentType: 'select',
    onChangeListenTo: ['tool_type'],
    zodValidation: audioTranscriptionConfigSchema.shape.language,
    afterOnChange: (_value, _i, form) => {
      // Simple approach: just validate, don't clear values
      // The backend already filters providers/models by language
      queueMicrotask(() => {
        form?.validateAllFields('change')
      })
    },
    inputProps: {
      placeholder: 'Select language',
      label: 'Language',
      options:
        languages?.map((language) => ({
          label: language.name,
          value: language.code,
          icon: <Flag iso={language.country} circle size={20} />,
        })) || [],
      'data-testid': 'select-audio-transcription-language',
    },
    visible: (state) => state.tool_type === 'audio_transcription',
  },
  {
    name: 'audio_transcription_config.model_id',
    componentType: 'selectWithGroups',
    onChangeListenTo: ['tool_type', 'audio_transcription_config.language'],
    zodValidation: audioTranscriptionConfigSchema.shape.model_id,
    getDynamicProps: () => {
      // Filter models based on selected language
      const filteredProviders =
        providers?.stt_providers
          ?.filter((provider) => SUPPORTED_STT_PROVIDERS.includes(provider.id))
          ?.filter((provider) => provider.models.length > 0) || []

      const availableModelIds = filteredProviders.flatMap((provider) =>
        provider.models.map((model) => model.id),
      )

      return {
        zodVal: invalidSelectValueSchema(
          audioTranscriptionConfigSchema.shape.model_id,
          availableModelIds,
        ),
        inputProps: {
          placeholder: 'Select model',
          label: 'Model',
          groups: filteredProviders.map((provider) => ({
            label: provider.label,
            options: provider.models.map((model) => ({
              label: (
                <div className="flex items-center gap-2">
                  <img src={provider.meta.icon_url} alt={provider.label} className="w-4 h-4" />
                  {capitalize(`${model.label}`)}
                </div>
              ),
              searchValue: `${provider.label} ${model.label}`,
              value: model.id,
            })),
          })),
        },
      }
    },
    eventDataExtractor: (value) => value,
    afterOnChange: (value, _, form) => {
      if (!form) return
      const provider = providers?.stt_providers.find((p) =>
        p.models.some((model) => model.id === value.audio_transcription_config?.model_id),
      )
      form.setFieldValue('audio_transcription_config.provider_id', provider?.id ?? '')
      form.validateAllFields('change')
    },
    visible: (state) => state.tool_type === 'audio_transcription',
  },
  {
    name: 'audio_transcription_config.audio_url',
    componentType: 'input',
    onChangeListenTo: ['tool_type'],
    zodValidation: audioTranscriptionConfigSchema.shape.audio_url,
    inputProps: {
      inputMode: 'url',
      placeholder: 'Enter audio URL',
      label: 'Audio URL',
      endIcon: (
        <NodevarList
          nodevars={nodeVars}
          inputElementSelector="#audio_transcription_config.audio_url"
        />
      ),
    },
    visible: (state) => state.tool_type === 'audio_transcription',
  },
]
